/** Typed authenticated transport for optional, connection-scoped onboarding answers. */
import * as z from 'zod'
import {
  merchantOnboardingAnswerSchemas,
  merchantOnboardingFieldKeySchema,
  type MerchantOnboardingAnswerInput,
} from '@testfeed/shopify-contracts'
import { captureAuthenticatedShopBootstrap } from '../../../authenticatedShopBootstrap'
import { shopifyApiFetch, type ShopifyAppBridge } from '../../../shopifyApiFetch'
import { throwMerchantFacingApiError } from '../../../shopifyApiError'
import { parseContractPayload } from '../../../merchantErrors'

const fieldSchema = z.object({ fieldKey: merchantOnboardingFieldKeySchema, answer: z.unknown() })
const statusSchema = z.object({
  status: z.enum(['not_started', 'in_progress', 'completed', 'dismissed']),
  version: z.literal(2),
  fields: z.array(fieldSchema),
  briefComplete: z.boolean(),
})
export type OnboardingField = z.infer<typeof fieldSchema>
export type OnboardingStatus = z.infer<typeof statusSchema>

/** Refuses malformed stored answers instead of silently showing fabricated empty fields. */
function parseStatus(payload: unknown): OnboardingStatus {
  const status = parseContractPayload('merchant-onboarding', statusSchema, payload)
  for (const field of status.fields) {
    if (!merchantOnboardingAnswerSchemas[field.fieldKey].safeParse(field.answer).success)
      throw new Error(`Invalid persisted onboarding answer for ${field.fieldKey}`)
  }
  return status
}

/** Shopify resolves the verified session; the server fences the exact expected connection. */
export async function readOnboardingDetails(
  shopify: ShopifyAppBridge,
  shop: string,
  connectionId: string,
  signal: AbortSignal,
) {
  const { authenticatedShopify } = await captureAuthenticatedShopBootstrap(shopify, { expectedShop: shop, signal })
  const query = new URLSearchParams({ expectedConnectionId: connectionId })
  const response = await shopifyApiFetch(authenticatedShopify, `/api/merchant-onboarding/scoped?${query}`, {
    init: { signal },
  })
  if (!response.ok) return throwMerchantFacingApiError(response, 'merchant-onboarding-read')
  return parseStatus(await response.json())
}

/** A changed connection rejects the write before any answer or lifecycle state changes. */
export async function writeOnboardingDetails(
  shopify: ShopifyAppBridge,
  shop: string,
  connectionId: string,
  action: { action: 'finish'; answers: MerchantOnboardingAnswerInput[] },
) {
  const { authenticatedShopify } = await captureAuthenticatedShopBootstrap(shopify, { expectedShop: shop })
  const response = await shopifyApiFetch(authenticatedShopify, '/api/merchant-onboarding/scoped', {
    init: {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...action, expectedConnectionId: connectionId }),
    },
  })
  if (!response.ok) return throwMerchantFacingApiError(response, 'merchant-onboarding-write')
  return parseStatus(await response.json())
}
